<template>
  <form>
    <div class="card card-hover-shadow card-bordered mx-auto w-100 py-3 shadow-lg ">
      <form-wizard shape="circle" :start-index="wizard == null ? 0 : 0" color="#ec9a3c" errorColor="#ec9a3c" @on-complete="wizardCompleted()">
        <template slot="title">
          <div class="media align-items-center">
            <div class="media-body">
              <h2 class="text-primary text-left pt-2">Getting Started...</h2>
            </div>
            <figure class="w-100 px-5 mmax-w-7rem mmr-3">
              <img src="/assets/svg/icons/icon-31.svg" alt="SVG" class="img-fluid">
            </figure>
          </div>
        </template>
        <template slot="custom-buttons-left" scope="props">
          <div class="wizard-footer-left">
            <span v-if="allowClose && props.activeTabIndex === 0" role="button" tabindex="0">
              <button tabindex="-1" type="button" class="wizard-btn bg-secondary" 
                      style="background-color:#596d82 !important; border-color: #596d82 !important; color: #fff"
                      @click="$emit('close')">{{ closeLabel }}</button>
            </span>
          </div>
        </template>
        <tab-content v-if="wizard == null" title="Introduction" icon="fas fa-clipboard-check" class="min-h-420rem">
          <div class="row">
            <div class="col text-navy">
              <h2>Let's get started...</h2>
              <p>
                By answering a few questions about your property, finances and goals we tailor
                our search to select the best mortgage deals just for you.
              </p>
              <p class="text-muted small">
                (the whole process is painless and will only take a minute)
              </p>
            </div>
            <div class="col">
              <figure class="w-100">
                <img src="/assets/svg/illustrations/get-answered.svg" alt="SVG" class="img-fluid">
<!--                <img src="/assets/svg/icons/icon-23.svg" alt="SVG" class="img-fluid">-->
              </figure>
            </div>
          </div>
        </tab-content>
        <tab-content title="Property" icon="fas fa-home" class="min-h-420rem" :before-change="validatePropertyStep">
          <div class="row">
            <div class="col">
              <fieldset>
                <h4>Your Property...</h4>
                <label for="propertyAddress" class="input-label">What is the address of your property?</label>
                <PostcodePicker id="propertyAddress"  :value="postcode.selection" @input="selectProperty($event)" :description="form.property.address" />
                <div class="form-group">
                  <label for="propertyType" class="input-label">Do you live in this property or is it an investment?</label>
                  <SelectButton id="propertyType" v-model="form.propertyType" :options="propertyTypeOptions" optionLabel="label" optionValue="value" />
                </div>
                <FormErrorAlert :valid="validation.property.valid" :error="validation.property.error"/>
              </fieldset>
            </div>
            <div class="col" align="center">
              <figure class="w-80">
                <img src="/assets/cmc/svg/icons/icon-house.svg" alt="SVG" class="img-fluid">
              </figure>
            </div>
          </div>
        </tab-content>
        <tab-content title="Mortgage" icon="fas fa-pound-sign" class="min-h-420rem" :before-change="validateMortgageStep">
          <div class="row">
            <div class="col">
              <fieldset>
                <h4>Your Mortgage...</h4>
                <div class="form-group">
                  <label for="propertyValue1" class="input-label">What is the current value of your property?</label>
                  <InputNumber id="propertyValue1" v-model="form.mortgage.propertyValue" placeholder="£ 0" prefix="£ " class="w-100" />
                </div>
                <div class="form-group">
                  <label for="remainingBalance1" class="input-label">What is your remaining mortgage balance?</label>
                  <InputNumber id="remainingBalance1" v-model="form.mortgage.remainingBalance" placeholder="£ 0" prefix="£ " class="w-100" />
                </div>
                <!-- End Form Group -->
                <!-- Form Group -->
                <div class="form-group">
                  <label for="term1" class="input-label">What is your remaining mortgage term?</label>
                  <InputNumber id="term1" v-model="form.mortgage.term" suffix=" yrs" placeholder="25 yrs" :min="5" :max="25" class="w-100" />
                </div>
                <FormErrorAlert :valid="validation.mortgage.valid" :error="validation.mortgage.error"/>
              </fieldset>
            </div>
            <div class="col" align="center">
              <figure class="w-80">
                <img src="/assets/cmc/svg/icons/icon-house.svg" alt="SVG" class="img-fluid">
              </figure>
            </div>
          </div>
        </tab-content>
        <tab-content title="Lender" icon="fas fa-building" class="min-h-420rem" :before-change="validateLenderStep">
          <div class="row">
            <div class="col">
              <fieldset>
                <h4>Your Current Lender...</h4>
                <div class="form-group">
                  <label class="input-label">Who is your current lender?</label>
                  <Dropdown v-model="form.mortgage.currentLender" :options="allLenders" optionLabel="lenderName" optionValue="lenderID" :filter="true" placeholder="Select a Lender" :showClear="true" class="w-100"/>
                </div>
                <div class="form-group">
                  <label for="currentMonthly" class="input-label">What is your current monthly repayment?</label>
                  <InputNumber id="currentMonthly" placeholder="£ 0" v-model="form.mortgage.currentMonthly" prefix="£ " class="w-100" :disabled="form.mortgage.currentLender == null" />
                </div>
                <div class="form-group">
                  <label for="currentEndDate" class="input-label">When does your fixed-term period end?</label>
                  <Calendar id="currentEndDate" v-model="form.mortgage.endOfFixedTerm" placeholder="mm/yyyy" view="month" dateFormat="mm/yy" :yearNavigator="true" yearRange="2020:2030" />
                </div>
                <FormErrorAlert :valid="validation.lender.valid" :error="validation.lender.error"/>
              </fieldset>
            </div>
            <div class="col" align="center">
              <figure class="w-80">
                <img src="/assets/svg/icons/icon-67.svg" alt="SVG" class="img-fluid">
              </figure>
            </div>
          </div>
        </tab-content>
        <tab-content title="Finances" icon="fas fa-piggy-bank" class="min-h-420rem" :before-change="validateFinancesStep">
          <div class="row">
            <div class="col">
              <fieldset>
                <h4>Your Financial Circumstances...</h4>
                <div class="form-group">
                  <label for="creditScore" class="input-label">What is your current credit score?</label>
                  <SelectButton id="creditScore" v-model="form.finances.creditScore" :options="creditScoreOptions" />
                </div>
                <label class="input-label mb-1">What is your employment status?</label>
                <div class="form-group">
                  <div v-for="n in employmentStatusOptions.length" :key="n" class="p-field-radiobutton">
                    <RadioButton :id="'employmentStatus' + n" name="employmentStatus" :value="employmentStatusOptions[n-1]" v-model="form.finances.employmentStatus" />
                    <label :for="'employmentStatus' + n" style="margin-bottom: unset;">{{ employmentStatusOptions[n-1] }}</label>
                  </div>
                </div>
                <FormErrorAlert :valid="validation.finances.valid" :error="validation.finances.error"/>
              </fieldset>
            </div>
            <div class="col" align="center">
              <figure class="w-80">
                <img src="/assets/svg/icons/icon-22.svg" alt="SVG" class="img-fluid">
              </figure>
            </div>
          </div>
        </tab-content>
        <tab-content title="Goals" icon="fas fa-compass" class="min-h-420rem" :before-change="validateGoalsStep">
          <div class="row">
            <div class="col">
              <fieldset>
                <h4>Your Remortgaging Goals...</h4>
                <!-- Price, Green, Fixed Rate, Well-known supplier, No early exit fees 
                -->
                <GoalSlider class="mb-2" 
                            label="I would like to minimise my monthly repayments." 
                            :value="form.goals.bestMonthly" 
                            @updated="form.goals.bestMonthly = $event" />
                <GoalSlider class="mb-2"
                            label="I would like to get the best deal over the life of the mortgage."
                            v-model="form.goals.bestLifetime"
                            @updated="form.goals.bestLifetime = $event"/>
                <GoalSlider class="mb-2" 
                            label="I would like a new fixed term deal." 
                            :value="form.goals.fixedTermDeal" 
                            @updated="form.goals.fixedTermDeal = $event" />
                <GoalSlider class="mb-2"
                            label="I would like to an ethical lender."
                            :value="form.goals.ethicalLender" 
                            @updated="form.goals.ethicalLender = $event"/>
                <GoalSlider class="mb-2"
                            label="I would like a well-known lender."
                            :value="form.goals.wellKnownLender"
                            @updated="form.goals.wellKnownLender = $event"/>
                <FormErrorAlert :valid="validation.goals.valid" :error="validation.goals.error"/>
              </fieldset>
            </div>
            <div class="col" align="center">
              <figure class="w-80">
                <img src="/assets/svg/icons/icon-1.svg" alt="SVG" class="img-fluid">
              </figure>
            </div>
          </div>
        </tab-content>
        <tab-content v-if="wizard == null" title="Summary" icon="fas fa-check" class="min-h-420rem" style="height: 100%; filter: grayscale(40%); background-image: url('/assets/svg/illustrations/relaxing-man.svg'); background-repeat: no-repeat; background-position: center;">
          <div class="" 
               >
            <div class="text-center w-95">
              <h2 class="font-size-4">Thank You!</h2>
              <h2 class="font-size-3">Now let's see what deals we can find for you...</h2>
            </div>
          </div>
        </tab-content>
      </form-wizard>
    </div>
  </form>
</template>

<script>
import {FormWizard, TabContent} from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import {mapGetters} from "vuex"
import InputNumber  from 'primevue/inputnumber'
import Calendar from 'primevue/calendar'
import Dropdown from 'primevue/dropdown'
import SelectButton from 'primevue/selectbutton'
import RadioButton from 'primevue/radiobutton'
import GoalSlider from "@/components/forms/GoalSlider";
import { PostcoderApi } from "@/services/PostcodeApi"
import PostcodePicker from "@/components/forms/PostcodePicker";
import {MortgageDetails, PropertyDetails} from "@/utils/MortgageWatchQuery.ts";
import FormErrorAlert from "@/components/forms/FormErrorAlert";

export default {
  name: "MortgageWizard",

  components: {
    FormErrorAlert,
    PostcodePicker,
    GoalSlider,
    RadioButton,
    SelectButton,
    Dropdown,
    Calendar,
    InputNumber,
    FormWizard,
    TabContent
  },

  props: {
    wizard: Object,
    closeLabel: {
      type: String,
      default: "Close"
    },
    allowClose: {
      type: Boolean,
      default: false
    }
  },
  
  mounted() {
    if (this.wizard != null) {
      this.form = { ...this.wizard };
    }
  },
  
  watch: {
    wizard: {
      deep: true,
      handler: function(val, oldVal) {
        this.form = { ...val };
      }
    }
  },
  
  created() {
    this.form.property = new MortgageDetails();
  },

  data() {
    return {
      postcode: {
        selection: null
      },
      validation: {
        property: {
          valid: null,
          error: "Please tell us about your property."
        },
        mortgage: {
          valid: null,
          error: "Please tell us about your mortgage."
        },
        lender: {
          valid: null,
          error: "Please tell us about your lenders."
        },
        finances: {
          valid: null,
          error: "Please tell us about your finances."
        },
        goals: {
          valid: null,
          error: "Please tell us about your remortgaging goals."
        },
      },
      form: {
        propertyType: null,
        property: {
          urn: null,
          name: null,
          postcode: null,
          address: null,
          longitude: null,
          latitude: null
        },
        mortgage: {
          propertyValue: null,
          remainingBalance: null,
          term: null,
          currentLender: null,
          currentMonthly: null,
          endOfFixedTerm: null,
        },
        finances: {
          creditScore: null,
          employmentStatus: null
        },
        goals: {
          bestMonthly: 100,
          bestLifetime: 25,
          fixedTermDeal: 80,
          ethicalLender: 25,
          wellKnownLender: 50
        }
      },
      propertyTypeOptions: [
        { label: "I live here", value: "home" },
        { label: "This is an investment", value: "investment"}  
      ],
      creditScoreOptions: [
          'Very Poor',
          'Poor',
          'Average',
          'Good',
          'Excellent'
      ],
      employmentStatusOptions: [
          'Employed',
          'Self-Employed',
          'Contractor',
          'Retired',
          'Unemployed'
      ]
    }
  },
  computed: {
    ...mapGetters([
      "allLenders"
    ])
  },

  methods: {
    validatePropertyStep() {
      return new Promise((resolve, reject) => {
        this.validation.property.valid = 
            this.form.propertyType != null && 
            this.form.property.urn != null;
        resolve(this.validation.property.valid);
      })
    },

    validateMortgageStep() {
      return new Promise((resolve, reject) => {
        this.validation.mortgage.valid = 
            this.form.mortgage.propertyValue !== null &&
            this.form.mortgage.remainingBalance !== null &&
            this.form.mortgage.term !== null;
        resolve(this.validation.mortgage.valid);
      })
    },
    validateLenderStep() {
      console.log("valdateLender");
      return new Promise((resolve, reject) => {
        this.validation.lender.valid =
            this.form.mortgage.currentLender !== null && 
            this.form.mortgage.currentMonthly !== null &&
            this.form.mortgage.endOfFixedTerm !== null;
        resolve(this.validation.lender.valid);
      })
    },
    validateFinancesStep() {
      return new Promise((resolve, reject) => {
        this.validation.finances.valid = 
            this.form.finances.creditScore !== null && 
            this.form.finances.employmentStatus !== null;
        resolve(this.validation.finances.valid);
      })
    },

    validateGoalsStep() {
      return new Promise((resolve, reject) => {
        this.validation.goals.valid = 
            this.form.goals.bestMonthly > 0 &&
            this.form.goals.bestLifetime > 0 &&
            this.form.goals.fixedTermDeal > 0 &&
            this.form.goals.ethicalLender > 0 &&
            this.form.goals.wellKnownLender > 0;
        resolve(this.validation.goals.valid);
      })
    },

    selectProperty(event) {
      console.log("selectProperty: " + JSON.stringify(event));
      const property = new PropertyDetails({
        urn: event.uniquedeliverypointreferencenumber,
        address: event.summaryline,
        postcode: event.postcode,
        longitude: Number(event.longitude),
        latitude: Number(event.latitude),
        postcoder: event
      });
      property.name = property.getName();
      console.log("PD = " + JSON.stringify(property));
      
      this.form.property = property;
    },

    wizardCompleted() {
      this.$emit("completed", this.form);
    }
  }
}
</script>

<style>
  .vue-form-wizard .wizard-header {
    display: none;
  }

  .min-h-420rem {
    min-height: 25.75rem;
  }
</style>
